// ── Bannières hero d'une boutique ─────────────────────────────────────
// On réutilise la table existante `accueil_bannieres` (zone = "hero").
// Chaque bannière = une image, un titre, un petit paragraphe, une
// description, un bouton optionnel, et une plage de dates optionnelle
// pour l'activer automatiquement sur une période de promo.

export interface HeroBanner {
  id: number;
  idcommerce: number;
  title: string;
  subtitle: string;
  description: string;
  ctaLabel: string;
  ctaUrl: string;
  image: string;
  /** Afficher le titre / paragraphe / description par-dessus l'image. */
  showText: boolean;
  /** Alignement du texte (responsive : reste lisible sur mobile). */
  textAlign: BannerTextAlign;
  order: number;
  active: boolean;
  /** Début de la période d'activation (ISO, vide = pas de début). */
  startDate: string;
  /** Fin de la période d'activation (ISO, vide = pas de fin). */
  endDate: string;
}

export type BannerTextAlign = 'left' | 'center' | 'right';

/** Bannière prête à l'affichage dans le slider public. */
export interface HeroBannerItem {
  id: number;
  image: string;
  title: string;
  subtitle: string;
  description: string;
  ctaLabel: string;
  ctaUrl: string;
  showText?: boolean;
  textAlign?: BannerTextAlign;
}

export const HERO_ZONE = 'hero';

export interface HeroBannerInput {
  title: string;
  subtitle: string;
  description: string;
  ctaLabel: string;
  ctaUrl: string;
  image: string;
  showText: boolean;
  textAlign: BannerTextAlign;
  active: boolean;
  startDate: string;
  endDate: string;
}

export type BannerScheduleState = 'none' | 'scheduled' | 'live' | 'expired';

export interface DefaultHeroSource {
  image?: string | null;
  title?: string | null;
  description?: string | null;
}

/**
 * Construit une bannière hero par défaut à partir de l'image d'identité de la
 * boutique, utilisée quand le commerçant n'a créé AUCUNE bannière dans
 * dashboard/banners. Renvoie null si aucune image d'identité n'est disponible
 * (dans ce cas, l'accueil garde son en-tête de repli habituel).
 */
export function buildDefaultHeroBanner(source: DefaultHeroSource): HeroBannerItem | null {
  const image = (source.image || '').trim();
  if (!image) return null;
  return {
    id: 0,
    image,
    title: (source.title || '').trim(),
    subtitle: (source.description || '').trim(),
    description: '',
    ctaLabel: '',
    ctaUrl: '',
  };
}

export function mapHeroBannerRow(row: Record<string, unknown>): HeroBanner {
  return {
    id: Number(row.id),
    idcommerce: Number(row.idcommerce),
    title: (row.titre as string) || '',
    subtitle: (row.sous_titre as string) || '',
    description: (row.description as string) || '',
    ctaLabel: (row.bouton_texte as string) || '',
    ctaUrl: (row.bouton_lien as string) || '',
    image: (row.image as string) || '',
    showText: row.afficher_texte == null ? true : Boolean(row.afficher_texte),
    textAlign: normalizeBannerAlign(row.alignement_texte as string),
    order: row.ordre != null ? Number(row.ordre) : 0,
    active: Number(row.statut) === 1,
    startDate: (row.date_debut as string) || '',
    endDate: (row.date_fin as string) || '',
  };
}

export function heroBannerToItem(banner: HeroBanner): HeroBannerItem {
  return {
    id: banner.id,
    image: banner.image,
    title: banner.title,
    subtitle: banner.subtitle,
    description: banner.description,
    ctaLabel: banner.ctaLabel,
    ctaUrl: banner.ctaUrl,
    showText: banner.showText,
    textAlign: banner.textAlign,
  };
}

/** Normalise une valeur d'alignement issue de la base. */
export function normalizeBannerAlign(value: string | null | undefined): BannerTextAlign {
  if (value === 'left' || value === 'right') return value;
  return 'center';
}

/** L'image doit-elle être affichée « en clair », sans voile sombre ? */
export function bannerHasVisibleText(item: HeroBannerItem): boolean {
  if (item.showText === false) return false;
  return Boolean(item.title || item.subtitle || item.description || item.ctaLabel);
}

/**
 * Une bannière est-elle dans sa plage de dates ?
 * - Pas de début → active dès maintenant.
 * - Pas de fin → active sans limite.
 */
export function isBannerWithinSchedule(
  banner: { startDate: string; endDate: string },
  now: Date = new Date(),
): boolean {
  const t = now.getTime();
  if (banner.startDate) {
    const s = new Date(banner.startDate).getTime();
    if (!Number.isNaN(s) && t < s) return false;
  }
  if (banner.endDate) {
    const e = new Date(banner.endDate).getTime();
    if (!Number.isNaN(e) && t > e) return false;
  }
  return true;
}

/** État de planification d'une bannière (pour l'affichage admin). */
export function getBannerScheduleState(
  banner: { startDate: string; endDate: string },
  now: Date = new Date(),
): BannerScheduleState {
  if (!banner.startDate && !banner.endDate) return 'none';
  const t = now.getTime();
  if (banner.startDate) {
    const s = new Date(banner.startDate).getTime();
    if (!Number.isNaN(s) && t < s) return 'scheduled';
  }
  if (banner.endDate) {
    const e = new Date(banner.endDate).getTime();
    if (!Number.isNaN(e) && t > e) return 'expired';
  }
  return 'live';
}

/**
 * Une bannière est affichable dès qu'elle a une image ou un titre,
 * qu'elle est active ET dans sa plage de dates.
 */
export function isBannerVisible(banner: HeroBanner): boolean {
  return (
    banner.active &&
    Boolean(banner.image.trim() || banner.title.trim()) &&
    isBannerWithinSchedule(banner)
  );
}